import { useState, useEffect, useCallback, useRef } from 'react';
import { getRedirectResult, onIdTokenChanged, multiFactor, signOut as firebaseSignOut } from 'firebase/auth';
import { auth } from '../firebase';

// Dev-note:
// `useAuth()` is used in multiple components. We must ensure redirect completion is only
// attempted once per page load, otherwise multiple hook instances can race to "consume"
// the same redirect result and make debugging harder.
let _redirectResultAttempted = false;

// Module-level cache of the last resolved auth state. When a new useAuth()
// instance mounts (e.g. ProtectedRoute remounting on navigation), it seeds
// from this cache instead of defaulting to isLoading: true — preventing a
// visible loading spinner for already-authenticated users. The cache is
// validated against auth.currentUser's UID so a stale user's state is never
// served to a different user. Cleared immediately on any sign-out path.
let _resolvedAuthState = null;

function _safeErr(err) {
  // Logging policy (security + debuggability):
  // - We want full error strings/stack traces for diagnosing SSO issues quickly.
  // - We MUST NOT print OAuth credentials/tokens (Firebase sometimes attaches `credential`).
  // - FirebaseError.message/stack generally do not include tokens, but nested objects might.
  const hasCredentialField = Boolean(err && (err.credential || err._tokenResponse || err.oauthCredential));
  return {
    name: err?.name,
    code: err?.code,
    message: err?.message,
    stack: err?.stack,
    customData: err?.customData
      ? {
          // Full email is useful during live pilot debugging and is not a secret.
          email: err.customData.email,
          providerId: err.customData.providerId,
        }
      : undefined,
    // Explicitly note that a credential existed but was redacted from logs.
    hasRedactedCredential: hasCredentialField,
  };
}

export function useAuth() {
  const [authState, setAuthState] = useState(() => {
    if (_resolvedAuthState && _resolvedAuthState.user?.uid === auth.currentUser?.uid) {
      return _resolvedAuthState;
    }
    return {
      isAuthenticated: false,
      isLoading: true,
      user: null,
      claims: null,
      error: null,
      errorType: null,
      isEmailVerified: false,
      isMFAEnrolled: false
    };
  });

  const retryCountRef = useRef(0);
  const maxRetries = 3;
  const retryTimeoutRef = useRef(null);

  // --- Resilience refs (prevent transient redirects during token refresh) ---
  //
  // lastKnownMfaEnrolledRef: "Sticky" MFA enrollment latch.
  //
  // Once MFA enrollment is confirmed (true), it stays true for the duration of the
  // session. It is never downgraded back to false by routine onIdTokenChanged events.
  // This prevents ProtectedRoute from redirecting to /set-up-mfa in background tabs
  // when Firebase cross-tab persistence sync provides a user object with incomplete
  // MFA enrollment data (enrolledFactors momentarily empty).
  //
  // The ref is seeded from sessionStorage so that tab discard/restore (where React
  // re-mounts everything) doesn't lose the enrollment context. sessionStorage is
  // per-tab and survives page reloads, but clears when the tab is closed — exactly
  // the right lifecycle for session-scoped state.
  //
  // Reset paths: explicit signOut, debounced null-user confirmation, initial null user.
  const lastKnownMfaEnrolledRef = useRef(
    (() => { try { return sessionStorage.getItem('__td_mfa_enrolled') === '1'; } catch (_) { return false; } })()
  );

  // lastUserUidRef: Tracks the UID of the last known authenticated user.
  // When onIdTokenChanged fires with null (which can happen briefly during token refresh),
  // we use this to distinguish a genuine sign-out from a transient null. A short debounce
  // delay gives the SDK time to recover before we actually reset auth state.
  const lastUserUidRef = useRef(null);
  const nullUserTimeoutRef = useRef(null);

  // updateInFlightRef: Prevents concurrent updateAuthState calls from racing.
  // SessionTimeout's forceTokenRefresh() calls updateAuthState(user, true), which triggers
  // the Firebase SDK to fire onIdTokenChanged → another updateAuthState(user). Without this
  // guard, both async calls can interleave and produce inconsistent state.
  const updateInFlightRef = useRef(false);

  // Helper to classify errors
  const classifyError = useCallback((error) => {
    const temporaryErrors = [
      'auth/network-request-failed',
      'auth/too-many-requests',
      'auth/internal-error',
      'auth/server-error',
      'auth/service-unavailable'
    ];
    
    return temporaryErrors.includes(error.code) ? 'temporary' : 'permanent';
  }, []);

  // Detect errors that mean the account/session has been *invalidated* server-side
  // (as opposed to a transient network/token glitch). These fire when an admin
  // disables the Auth account and/or revokes refresh tokens: the next token
  // refresh fails permanently with one of these codes. In that case we must NOT
  // preserve partial auth state — we sign the user out so their elevated session
  // can no longer read data or drive the UI. This is the client-side companion to
  // the backend disable flow (auth.update_user(disabled=True) + revoke_refresh_tokens).
  const isAccountInvalidatedError = useCallback((error) => {
    const invalidatingCodes = [
      'auth/user-disabled',
      'auth/user-token-expired',
      'auth/id-token-revoked',
      'auth/user-not-found',
      'auth/invalid-user-token',
      'auth/session-cookie-revoked',
    ];
    return invalidatingCodes.includes(error?.code);
  }, []);

  // Smart token refresh - only force when explicitly needed
  const refreshToken = useCallback(async (user, forceRefresh = false) => {
    try {
      const idTokenResult = await user.getIdTokenResult(forceRefresh);
      return idTokenResult;
    } catch (error) {
      console.error('Token refresh failed:', error);
      throw error;
    }
  }, []);

  // Retry logic for temporary failures
  const retryAuthUpdate = useCallback(async (user, delay = 1000) => {
    if (retryCountRef.current >= maxRetries) {
      console.log('Max retries reached, giving up');
      return null;
    }

    retryCountRef.current += 1;
    console.log(`Retrying auth update (attempt ${retryCountRef.current}/${maxRetries}) in ${delay}ms`);

    return new Promise((resolve) => {
      retryTimeoutRef.current = setTimeout(async () => {
        try {
          const idTokenResult = await refreshToken(user, false);
          retryCountRef.current = 0; // Reset on success
          resolve(idTokenResult);
        } catch (error) {
          console.error(`Retry ${retryCountRef.current} failed:`, error);
          resolve(await retryAuthUpdate(user, delay * 2)); // Exponential backoff
        }
      }, delay);
    });
  }, [refreshToken]);

  const updateAuthState = useCallback(async (user, forceTokenRefresh = false) => {
    // In-flight guard: SessionTimeout's forceTokenRefresh() and the Firebase SDK's
    // onIdTokenChanged can both trigger updateAuthState concurrently. Serialising these
    // calls prevents interleaved state updates that could produce inconsistent auth state.
    if (updateInFlightRef.current) {
      console.log('[TD-SSO] useAuth: updateAuthState already in flight, skipping duplicate call');
      return;
    }
    updateInFlightRef.current = true;
    const startTime = Date.now();

    try {
      if (user) {
        // We have a valid user — cancel any pending null-user debounce timeout,
        // since the user clearly hasn't signed out.
        if (nullUserTimeoutRef.current) {
          clearTimeout(nullUserTimeoutRef.current);
          nullUserTimeoutRef.current = null;
        }
        lastUserUidRef.current = user.uid;

        let idTokenResult;
        
        try {
          // Try to get token (only force refresh if explicitly requested)
          idTokenResult = await refreshToken(user, forceTokenRefresh);
        } catch (tokenError) {
          const errorType = classifyError(tokenError);
          
          if (errorType === 'temporary') {
            console.log('Temporary token error, attempting retry...');
            idTokenResult = await retryAuthUpdate(user);
            
            if (!idTokenResult) {
              // Even retries failed, but user might still be valid
              console.log('Token refresh failed after retries, using cached data');
              setAuthState(prev => ({
                ...prev,
                isLoading: false,
                error: 'Connection issues. Some features may be limited.',
                errorType: 'temporary'
              }));
              return;
            }
          } else {
            throw tokenError; // Permanent error, propagate it
          }
        }

        // Check MFA enrollment — "sticky" latch.
        //
        // MFA enrollment cannot legitimately change from true → false during an
        // active session. Every removal path (VerifyAction revert, admin script,
        // SwitchToTotpDialog) either never produces an empty enrolledFactors array
        // mid-session or redirects to sign-in afterward.
        //
        // However, cross-tab Firebase persistence sync can momentarily provide a
        // user object with an empty enrolledFactors array. If we accepted that as
        // genuine, ProtectedRoute would redirect to /set-up-mfa in background tabs.
        //
        // Three branches handle this:
        //   1. Currently enrolled → accept & persist (ref + sessionStorage)
        //   2. Not enrolled AND was never enrolled this session → accept false
        //   3. Not enrolled BUT was previously enrolled → cross-tab artifact, keep true
        let mfaEnrolled = lastKnownMfaEnrolledRef.current;
        try {
          const enrolledFactors = multiFactor(user).enrolledFactors;
          const currentlyEnrolled = Array.isArray(enrolledFactors) && enrolledFactors.length > 0;

          if (currentlyEnrolled) {
            mfaEnrolled = true;
            lastKnownMfaEnrolledRef.current = true;
            try { sessionStorage.setItem('__td_mfa_enrolled', '1'); } catch (_) {}
          } else if (!lastKnownMfaEnrolledRef.current) {
            mfaEnrolled = false;
          } else {
            console.warn(
              '[TD-SSO] useAuth: MFA appears unenrolled but was previously enrolled;',
              'preserving enrolled state (cross-tab sync artifact)'
            );
            mfaEnrolled = true;
          }
        } catch (mfaCheckError) {
          console.warn(
            '[TD-SSO] useAuth: MFA check failed, preserving last known value:',
            lastKnownMfaEnrolledRef.current,
            mfaCheckError?.message
          );
          mfaEnrolled = lastKnownMfaEnrolledRef.current;
        }

        // // High-signal auth state logging (non-sensitive):
        // // Helps debug "signed in but redirected to /login" issues quickly.
        // try {
        //   const providerIds = (user.providerData || []).map(p => p?.providerId).filter(Boolean);
        //   console.log('[TD-SSO] useAuth:updateAuthState user present', {
        //     uid: user.uid,
        //     emailDomain: _safeEmailDomain(user.email),
        //     emailVerified: Boolean(user.emailVerified),
        //     providerIds,
        //     forceTokenRefresh: Boolean(forceTokenRefresh),
        //   });
        // } catch (_) {}

        // Debug helper (non-production only):
        // In the modular Firebase SDK, there's no global `window.firebase` object.
        // Exposing the claims here makes it much easier to diagnose Firestore rule issues
        // (e.g. organisation/accountType/userType mismatches) in staging/dev without
        // shipping any sensitive tooling to production.
        try {
          const env = import.meta.env.VITE_ENVIRONMENT || process.env.NODE_ENV;
          const isProd = (env === 'production');
          if (!isProd && typeof window !== 'undefined') {
            window.__td = window.__td || {};
            window.__td.authClaims = idTokenResult.claims;
            window.__td.uid = user.uid;
          }
        } catch (_) {}
        
        const resolvedState = {
          isAuthenticated: true,
          isLoading: false,
          user,
          claims: idTokenResult.claims,
          error: null,
          errorType: null,
          isEmailVerified: user.emailVerified,
          isMFAEnrolled: mfaEnrolled,
          hasSetOwnPassword: idTokenResult.claims.hasSetOwnPassword
        };
        setAuthState(resolvedState);
        _resolvedAuthState = resolvedState;

        // try {
        //   console.log('[TD-SSO] useAuth:updateAuthState claims summary', {
        //     uid: user.uid,
        //     accountType: idTokenResult?.claims?.accountType,
        //     userType: idTokenResult?.claims?.userType,
        //     organisation: idTokenResult?.claims?.organisation,
        //     authMode: idTokenResult?.claims?.authMode,
        //     hasSetOwnPassword: Boolean(idTokenResult?.claims?.hasSetOwnPassword),
        //     hasCompletedSetup: Boolean(idTokenResult?.claims?.hasCompletedSetup),
        //     isAssignableClinician: idTokenResult?.claims?.isAssignableClinician,
        //   });
        // } catch (_) {}

        // Reset retry count on successful update
        retryCountRef.current = 0;
      } else {
        // Null user: could be a genuine sign-out OR a transient blip during token refresh.
        //
        // If we previously had a valid user (lastUserUidRef is set), don't immediately nuke
        // auth state. Instead, debounce for a short window (2 seconds) and re-check
        // auth.currentUser. If the SDK has recovered by then, we skip the reset entirely.
        // This prevents ProtectedRoute from redirecting users to /login or /set-up-mfa
        // during routine token refresh cycles.
        if (lastUserUidRef.current) {
          // Clear the module cache immediately so any component that mounts
          // during the debounce window does NOT seed from stale auth state.
          _resolvedAuthState = null;
          console.log('[TD-SSO] useAuth: null user after valid session, debouncing before reset...');
          // Clear any existing debounce to avoid stacking timeouts
          if (nullUserTimeoutRef.current) {
            clearTimeout(nullUserTimeoutRef.current);
          }
          nullUserTimeoutRef.current = setTimeout(() => {
            nullUserTimeoutRef.current = null;
            // Re-check: has the SDK recovered with a valid user?
            if (auth.currentUser) {
              console.log('[TD-SSO] useAuth: user recovered after debounce, skipping reset');
              return;
            }
            // Genuine sign-out — reset everything
            console.log('[TD-SSO] useAuth: confirmed sign-out after debounce, resetting auth state');
            lastUserUidRef.current = null;
            lastKnownMfaEnrolledRef.current = false;
            _resolvedAuthState = null;
            try { sessionStorage.removeItem('__td_mfa_enrolled'); } catch (_) {}
            setAuthState({
              isAuthenticated: false,
              isLoading: false,
              user: null,
              claims: null,
              error: null,
              errorType: null,
              isEmailVerified: false,
              isMFAEnrolled: false,
              hasSetOwnPassword: false
            });
          }, 2000);
        } else {
          // No prior user — this is the initial load with no signed-in user, or
          // a confirmed sign-out that already cleared lastUserUidRef. Reset immediately.
          console.log('No user, resetting auth state');
          lastKnownMfaEnrolledRef.current = false;
          _resolvedAuthState = null;
          try { sessionStorage.removeItem('__td_mfa_enrolled'); } catch (_) {}
          setAuthState({
            isAuthenticated: false,
            isLoading: false,
            user: null,
            claims: null,
            error: null,
            errorType: null,
            isEmailVerified: false,
            isMFAEnrolled: false,
            hasSetOwnPassword: false
          });
        }
      }
    } catch (error) {
      console.error('Auth state error:', error);
      const errorType = classifyError(error);
      
      // SECURITY: if the error means the account/session was invalidated
      // server-side (admin disabled the account or revoked refresh tokens),
      // do NOT preserve partial auth state — force a full sign-out. Otherwise a
      // just-disabled user could linger with a usable (if degraded) session.
      if (user && isAccountInvalidatedError(error)) {
        console.warn('[TD-SSO] useAuth: account invalidated server-side, signing out:', error?.code);
        lastUserUidRef.current = null;
        lastKnownMfaEnrolledRef.current = false;
        _resolvedAuthState = null;
        try { sessionStorage.removeItem('__td_mfa_enrolled'); } catch (_) {}
        try { await firebaseSignOut(auth); } catch (signOutErr) {
          console.error('[TD-SSO] useAuth: sign-out after account invalidation failed:', signOutErr);
        }
        setAuthState({
          isAuthenticated: false,
          isLoading: false,
          user: null,
          claims: null,
          error: error.message,
          errorType,
          isEmailVerified: false,
          isMFAEnrolled: false,
          hasSetOwnPassword: false,
        });
      } else if (user) {
        // IMPORTANT: for other errors, if we still have a user object, don't
        // completely log them out. This prevents transient errors (network
        // glitches, race conditions in multi-tab scenarios) from causing
        // unexpected logouts.
        console.warn('[TD-SSO] useAuth: Error during auth state update but user exists, preserving partial auth state');
        setAuthState(prev => ({
          ...prev,
          isLoading: false,
          error: error.message,
          errorType,
          // Keep the user authenticated to prevent unexpected redirects
          // The error state can be used by components to show appropriate UI
        }));
      } else {
        setAuthState({
          isAuthenticated: false,
          isLoading: false,
          user: null,
          claims: null,
          error: error.message,
          errorType,
          isEmailVerified: false,
          isMFAEnrolled: false
        });
      }
    } finally {
      // Release the in-flight guard so the next call can proceed
      updateInFlightRef.current = false;
    }
  }, [refreshToken, classifyError, retryAuthUpdate, isAccountInvalidatedError]);

  // Complete any pending OAuth redirect sign-in (Microsoft/Google/etc).
  //
  // Why this lives here (and not only on the sign-in page):
  // - Your `/` and `/login` routes are wrapped by `AutoLoginRedirect`, which shows a loading
  //   spinner while `useAuth` resolves auth state.
  // - If we only call `getRedirectResult()` inside the sign-in page component, that component
  //   may not mount during the loading spinner, so redirect completion never runs.
  //
  // By calling `getRedirectResult()` here, we guarantee redirect completion runs as soon as
  // the app boots, regardless of which route/component is currently displayed.
  useEffect(() => {
    (async () => {
      try {
        if (_redirectResultAttempted) return;
        _redirectResultAttempted = true;
        console.log('[TD-SSO] useAuth: attempting getRedirectResult()');
        if (typeof window !== 'undefined') {
          try {
            const u = new URL(window.location.href);
            const keys = Array.from(u.searchParams.keys());
            console.log('[TD-SSO] useAuth: current URL (debug):', {
              pathname: u.pathname,
              searchKeys: keys,
              hashPresent: Boolean(u.hash && u.hash.length > 1),
            });
          } catch (_) {}
        }
        let msRedirectStartedAt = null;
        let msRedirectTenant = null;
        try {
          msRedirectStartedAt = sessionStorage.getItem('__td_ms_redirect_started');
          msRedirectTenant = sessionStorage.getItem('__td_ms_redirect_tenant');
        } catch (_) {}
        if (msRedirectStartedAt) {
          console.log('[TD-SSO] useAuth: detected prior Microsoft redirect attempt', {
            msRedirectStartedAt,
            msRedirectTenant,
          });
        }

        // // Log storage keys to verify whether Firebase stored the redirect event id.
        // // This is extremely useful when `getRedirectResult()` returns no user.
        // try {
        //   const ssKeys = Object.keys(sessionStorage || {}).filter(k =>
        //     k.toLowerCase().includes('firebase') || k.toLowerCase().includes('redirect')
        //   );
        //   const lsKeys = Object.keys(localStorage || {}).filter(k =>
        //     k.toLowerCase().includes('firebase') || k.toLowerCase().includes('redirect')
        //   );
        //   console.log('[TD-SSO] useAuth: storage keys (debug)', { sessionStorage: ssKeys, localStorage: lsKeys });

        //   // If present, log the pendingRedirect value (not sensitive; it is an internal marker).
        //   const pendingKey = ssKeys.find(k => k.startsWith('firebase:pendingRedirect:'));
        //   if (pendingKey) {
        //     try {
        //       console.log('[TD-SSO] useAuth: pendingRedirect value (debug)', {
        //         key: pendingKey,
        //         value: sessionStorage.getItem(pendingKey),
        //       });
        //     } catch (_) {}
        //   }
        // } catch (e) {
        //   console.warn('[TD-SSO] useAuth: could not enumerate web storage keys:', _safeErr(e));
        // }

        const tryGetRedirect = async (attemptLabel) => {
          const r = await getRedirectResult(auth);
          if (r?.user) {
            try {
              // const providerIds = (r.user.providerData || []).map(p => p?.providerId).filter(Boolean);
              console.log(`[TD-SSO] useAuth: getRedirectResult completed (${attemptLabel})`, {
                uid: r.user.uid,
                // emailDomain: _safeEmailDomain(r.user.email),
                // providerIds,
              });
            } catch (_) {
              console.log(`[TD-SSO] useAuth: getRedirectResult completed (${attemptLabel}) for uid:`, r.user.uid);
            }
            await updateAuthState(r.user, true);
            try {
              sessionStorage.removeItem('__td_ms_redirect_started');
              sessionStorage.removeItem('__td_ms_redirect_tenant');
            } catch (_) {}
            return true;
          }
          return false;
        };

        // Attempt immediately first.
        const gotUser = await tryGetRedirect('immediate');
        if (!gotUser) {
          console.log('[TD-SSO] useAuth: getRedirectResult() returned no user (normal if no redirect pending)');
          if (msRedirectStartedAt) {
            console.warn('[TD-SSO] useAuth: redirect started but no redirect result. Likely blocked storage, cancelled flow, or misconfigured OAuth provider.');
            // One retry after a short delay. This addresses rare timing races where Auth
            // initialization/persistence hasn't completed yet on page load.
            await new Promise((res) => setTimeout(res, 500));
            const gotOnRetry = await tryGetRedirect('retry');
            if (!gotOnRetry) {
              console.warn('[TD-SSO] useAuth: retry also returned no user. Likely not a timing issue.');
            }
          }
        }
      } catch (err) {
        // It's common for there to be no redirect pending; but when there IS a redirect pending,
        // this is the most important place to capture error detail (e.g. account linking failures).
        console.warn('[TD-SSO] useAuth: getRedirectResult error (safe, full strings):', _safeErr(err));
      }
    })();
  }, [updateAuthState]);

  useEffect(() => {
    // Use only onIdTokenChanged - covers both auth state changes AND token refresh
    const unsubscribeTokenChange = onIdTokenChanged(auth, async (user) => {
      // console.log('[TD-SSO] useAuth: Auth/Token state changed:', user?.uid);
      await updateAuthState(user);
    });

    // Cleanup all pending timeouts on unmount
    return () => {
      unsubscribeTokenChange();
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
      if (nullUserTimeoutRef.current) {
        clearTimeout(nullUserTimeoutRef.current);
      }
    };
  }, [updateAuthState]);

  // Helper functions to check roles
  const isProvider = () => {
    return authState.claims?.accountType === 'Provider';
  };

  const isServiceUser = () => {
    return authState.claims?.accountType === 'Service User';
  };

  const isOrgAdmin = () => {
    return isProvider() && authState.claims?.userType === 'Org. Admin';
  };

  const isClinician = () => {
    return isProvider() && authState.claims?.userType === 'Clinician';
  };

  const isSupportUser = () => {
    return isProvider() && authState.claims?.userType === 'Support';
  };

  const isPatient = () => {
    return isServiceUser() && authState.claims?.userType === 'Patient';
  };

  const isInformant = () => {
    return isServiceUser() && authState.claims?.userType === 'Informant';
  };

  // RMS-specific helpers (canonical discriminators using accountType)
  const isRmsProvider = () => {
    return authState.claims?.accountType === 'RMS Provider User';
  };

  const isRmsServiceUser = () => {
    return authState.claims?.accountType === 'RMS Service User';
  };

  const signOut = async () => {
    try {
      await firebaseSignOut(auth);
      try { sessionStorage.removeItem('__td_mfa_enrolled'); } catch (_) {}
    } catch (error) {
      console.error('Error signing out:', error);
      throw error;
    }
  };

  // Force token refresh (useful after password changes, claims updates)
  const forceTokenRefresh = useCallback(async () => {
    if (authState.user) {
      console.log('Forcing token refresh');
      await updateAuthState(authState.user, true);
    }
  }, [authState.user, updateAuthState]);

  // Non-forced token refresh for periodic checks (e.g. SessionTimeout).
  // Uses getIdTokenResult(false) under the hood, which returns the cached
  // token if it is still valid and only makes a network call when the token
  // is expired or within 5 minutes of expiry. For Microsoft SSO users this
  // avoids unnecessary Entra roundtrips that can destabilise auth state
  // mid-session, while still ensuring expired tokens get refreshed.
  const softTokenRefresh = useCallback(async () => {
    if (authState.user) {
      await updateAuthState(authState.user, false);
    }
  }, [authState.user, updateAuthState]);

  // Retry authentication (useful for temporary error recovery)
  const retryAuth = useCallback(async () => {
    if (authState.user) {
      console.log('Retrying authentication');
      retryCountRef.current = 0; // Reset retry count
      await updateAuthState(authState.user);
    }
  }, [authState.user, updateAuthState]);

  // Clear temporary errors
  const clearError = useCallback(() => {
    if (authState.errorType === 'temporary') {
      setAuthState(prev => ({
        ...prev,
        error: null,
        errorType: null
      }));
    }
  }, [authState.errorType]);

  return {
    ...authState,
    isProvider,
    isServiceUser,
    isOrgAdmin,
    isClinician,
    isSupportUser,
    isPatient,
    isInformant,
    isRmsProvider,
    isRmsServiceUser,
    signOut,
    forceTokenRefresh,
    softTokenRefresh,
    retryAuth,
    clearError
  };
}